import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Wallet, Info, Check, Link2, Hand, AlertTriangle } from 'lucide-react';
import {
  Card, CardHeader, CardBody, Button, Field, Input, Textarea, ErrorNotice, Loading,
} from '../../components/ui/index.js';
import { PageHero } from '../../components/PageHero.jsx';
import { MethodIcon } from '../../checkout/MethodIcon.jsx';
import { useResource, useMutation } from '../../hooks/useApi.js';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges.js';
import { useToast } from '../../components/ToastProvider.jsx';
import { http } from '../../lib/api.js';

const EASE = [0.2, 0.7, 0.2, 1];

/** A route is identified in the UI by one string, so it can back a radio group. */
const routeKey = (route) => (route.kind === 'offline' ? 'offline' : `online:${route.provider}`);
const parseRouteKey = (key) =>
  (key === 'offline'
    ? { kind: 'offline', provider: null }
    : { kind: 'online', provider: key.slice('online:'.length) });

/** A switch, not a checkbox — this turns real money on and off. */
function Toggle({ checked, onChange, label, disabled, size = 'md' }) {
  const w = size === 'sm' ? 38 : 44;
  const knob = size === 'sm' ? 14 : 16;
  const travel = w - knob - 8;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="relative shrink-0 rounded-full border transition-colors disabled:opacity-50"
      style={{
        width: w,
        height: knob + 8,
        background: checked ? 'rgb(var(--ink))' : 'rgb(var(--surface-2))',
        borderColor: checked ? 'rgb(var(--ink))' : 'rgb(var(--line))',
      }}
    >
      <motion.span
        className="absolute top-1/2 block rounded-full bg-white shadow-sm"
        style={{ width: knob, height: knob, translateY: '-50%' }}
        animate={{ left: checked ? travel + 4 : 4 }}
        transition={{ duration: 0.18, ease: EASE }}
      />
    </button>
  );
}

/**
 * One way of fulfilling a method: the merchant themselves, or a gateway.
 *
 * Both are offered as the same kind of choice deliberately. A shop taking UPI
 * into its own account and a shop taking UPI through a gateway are doing the
 * same thing from the customer's side; the only difference is who confirms it,
 * which is what the second line of each option says.
 */
function RouteOption({ option, selected, onSelect, disabled }) {
  const broken = option.kind === 'online' && option.status !== 'connected';

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={() => onSelect(routeKey(option))}
      className={`flex w-full items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors disabled:opacity-50 ${
        selected ? 'border-ink bg-surface-2' : 'border-line hover:bg-surface-2'
      }`}
    >
      <span
        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
          selected ? 'border-ink bg-ink text-white' : 'border-line'
        }`}
      >
        {selected && <Check className="h-2.5 w-2.5" strokeWidth={3} aria-hidden="true" />}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="text-sm font-medium text-ink">{option.label}</span>
          {broken && <span className="chip-danger">Not connected</span>}
        </span>
        <span className="mt-0.5 flex items-center gap-1.5 text-xs text-muted">
          {option.kind === 'offline'
            ? <Hand className="h-3 w-3 shrink-0" aria-hidden="true" />
            : <Link2 className="h-3 w-3 shrink-0" aria-hidden="true" />}
          {option.kind === 'offline'
            ? (option.blurb ?? 'You mark it received when the money arrives.')
            : 'Confirmed automatically when the payment goes through.'}
        </span>
      </span>
    </button>
  );
}

/** Where a method is offered. Two surfaces, and they are genuinely different. */
function SurfaceRow({ label, blurb, checked, onChange, disabled }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <div className="min-w-0">
        <div className="text-sm text-ink">{label}</div>
        <div className="text-xs text-muted">{blurb}</div>
      </div>
      <Toggle size="sm" checked={checked} onChange={onChange} label={label} disabled={disabled} />
    </div>
  );
}

/**
 * One method: whether it is on, who fulfils it, and where it appears.
 *
 * Fields are rendered from the definition the API sends rather than hardcoded
 * (PP-1, G6), so adding a method stays a backend change — and so a method whose
 * route is a gateway never shows bank details, because the gateway holds them.
 */
function MethodCard({ method, onSaved, onDirtyChange }) {
  const toast = useToast();

  const [enabled, setEnabled] = useState(method.enabled);
  const [route, setRoute] = useState(routeKey(method.route));
  const [details, setDetails] = useState(method.details ?? {});
  const [instructions, setInstructions] = useState(method.instructions ?? '');
  const [availability, setAvailability] = useState(method.availability);
  const [needs, setNeeds] = useState(null);

  // A refetch after saving hands down fresh props; local edits should follow.
  useEffect(() => {
    setEnabled(method.enabled);
    setRoute(routeKey(method.route));
    setDetails(method.details ?? {});
    setInstructions(method.instructions ?? '');
    setAvailability(method.availability);
  }, [method]);

  const offline = route === 'offline';
  const fields = offline ? method.offlineFields : [];

  const dirty =
    enabled !== method.enabled ||
    route !== routeKey(method.route) ||
    availability.online !== method.availability.online ||
    availability.pos !== method.availability.pos ||
    (offline && instructions !== (method.instructions ?? '')) ||
    fields.some((f) => (details[f.key] ?? '') !== (method.details?.[f.key] ?? ''));

  /**
   * The page above needs to know, because the warning belongs to the browser
   * tab rather than to one card.
   */
  useEffect(() => {
    onDirtyChange?.(method.key, dirty);
    return () => onDirtyChange?.(method.key, false);
  }, [dirty, method.key, onDirtyChange]);

  const save = useMutation(async (patch = {}) => {
    const next = { enabled, route, details, instructions, availability, ...patch };
    const updated = await http.put(`/dashboard/payment-methods/${method.key}`, {
      enabled: next.enabled,
      route: parseRouteKey(next.route),
      // A gateway route stores nothing of its own here: the details and the
      // instructions describe a merchant collecting the money themselves.
      details: next.route === 'offline' ? next.details : {},
      instructions: next.route === 'offline' ? next.instructions : '',
      availability: next.availability,
    });
    onSaved?.(updated);
    return updated;
  });

  /**
   * Missing required details are shown, not saved around.
   *
   * Turning on a method with an empty required field used to do precisely
   * nothing and say precisely nothing — the switch moved and no request went
   * out. Now the switch stays where the merchant put it and the card says what
   * it still needs.
   */
  function missingFor(nextRoute, nextDetails) {
    if (nextRoute !== 'offline') return [];
    return method.offlineFields
      .filter((f) => f.required && !String(nextDetails[f.key] ?? '').trim())
      .map((f) => f.label);
  }

  /** Applies a change optimistically and saves it, rolling back if it fails. */
  function persist(patch, { toastOn = false } = {}) {
    const before = { enabled, route, availability };
    if ('enabled' in patch) setEnabled(patch.enabled);
    if ('route' in patch) setRoute(patch.route);
    if ('availability' in patch) setAvailability(patch.availability);
    setNeeds(null);

    const nextEnabled = patch.enabled ?? enabled;
    const nextRoute = patch.route ?? route;
    const missing = missingFor(nextRoute, details);

    if (nextEnabled && missing.length > 0) {
      setNeeds(missing);
      return;
    }

    save.mutate(patch)
      .then(() => {
        if (!toastOn) return;
        toast.success(nextEnabled ? `${method.label} is on` : `${method.label} is off`, {
          description: nextEnabled
            ? 'Customers will see it where you have switched it on.'
            : 'Customers will no longer see it.',
        });
      })
      .catch(() => {
        setEnabled(before.enabled);
        setRoute(before.route);
        setAvailability(before.availability);
      });
  }

  /**
   * A method nothing can fulfil, shown rather than hidden.
   *
   * Card with no gateway connected is the common case, and a merchant looking
   * for card and not finding it assumes the product cannot do it.
   */
  if (!method.available) {
    return (
      <Card className="opacity-80">
        <CardHeader
          title={method.label}
          blurb={method.blurb}
          icon={() => <MethodIcon icon={method.icon} className="h-3.5 w-3.5" />}
          actions={<span className="chip-neutral">Unavailable</span>}
        />
        <CardBody>
          <div className="flex items-start gap-2.5 rounded-lg border border-line bg-surface-2 px-3 py-2.5">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-faint" aria-hidden="true" />
            <p className="text-xs leading-relaxed text-ink-soft">
              {method.label} always needs a payment provider, and none of the ones you have
              connected can take it.{' '}
              <Link className="underline underline-offset-2" to="/providers">Connect a provider</Link>
              {' '}to switch it on.
            </p>
          </div>
        </CardBody>
      </Card>
    );
  }

  const selectedRoute = method.routes.find((r) => routeKey(r) === route);
  const savedRouteBroken = selectedRoute?.kind === 'online' && selectedRoute.status !== 'connected';

  return (
    <Card>
      <CardHeader
        title={method.label}
        blurb={method.blurb}
        icon={() => <MethodIcon icon={method.icon} className="h-3.5 w-3.5" />}
        actions={
          <>
            <span className={enabled ? 'chip-success' : 'chip-neutral'}>{enabled ? 'On' : 'Off'}</span>
            <Toggle
              checked={enabled}
              onChange={(next) => persist({ enabled: next }, { toastOn: true })}
              label={`Accept ${method.label}`}
              disabled={save.loading}
            />
          </>
        }
      />

      <AnimatePresence initial={false}>
        {enabled && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.24, ease: EASE }}
            className="overflow-hidden"
          >
            <CardBody className="space-y-5">
              {save.error && <ErrorNotice error={save.error} title={`Could not save ${method.label}`} />}

              {needs && (
                <div role="alert" className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
                  <p className="text-sm leading-relaxed text-amber-800">
                    {method.label} needs {needs.join(' and ')} before customers can use it.
                    Fill {needs.length === 1 ? 'it' : 'them'} in, then save.
                  </p>
                </div>
              )}

              {/* ------------------------- who fulfils it ------------------------ */}
              <div>
                <div className="mb-2">
                  <div className="text-xs font-medium uppercase tracking-wide text-faint">Who takes it</div>
                  {method.routes.length > 1 && (
                    <p className="mt-1 text-xs text-muted">
                      Your customer sees “{method.customerLabel}” either way. This decides where the
                      money lands and who confirms it arrived.
                    </p>
                  )}
                </div>

                <div role="radiogroup" aria-label={`Who takes ${method.label}`} className="space-y-2">
                  {method.routes.map((option) => (
                    <RouteOption
                      key={routeKey(option)}
                      option={option}
                      selected={routeKey(option) === route}
                      onSelect={(key) => persist({ route: key })}
                      disabled={save.loading}
                    />
                  ))}
                </div>

                {savedRouteBroken && (
                  <div className="mt-2 flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-700" aria-hidden="true" />
                    <p className="text-xs leading-relaxed text-red-800">
                      That provider is not connecting. Payments on this method will fail until it is
                      fixed under Payment providers.
                    </p>
                  </div>
                )}
              </div>

              {/* --------------------------- where ------------------------------ */}
              <div className="border-t border-line pt-4">
                <div className="text-xs font-medium uppercase tracking-wide text-faint">Where it appears</div>
                <div className="mt-1 divide-y divide-line">
                  <SurfaceRow
                    label="Payment links and checkout"
                    blurb="Anyone you send a link to can choose it."
                    checked={availability.online}
                    disabled={save.loading}
                    onChange={(next) => persist({ availability: { ...availability, online: next } })}
                  />
                  <SurfaceRow
                    label="At the till"
                    blurb="Your staff can ring it up on the POS."
                    checked={availability.pos}
                    disabled={save.loading}
                    onChange={(next) => persist({ availability: { ...availability, pos: next } })}
                  />
                </div>
                {!availability.online && !availability.pos && (
                  <p className="mt-2 text-xs text-amber-700">
                    Switched on but offered nowhere — nobody can pay by {method.label.toLowerCase()}.
                  </p>
                )}
              </div>

              {/* --------------- what the merchant collects themselves ----------- */}
              {offline && (
                <div className="space-y-4 border-t border-line pt-4">
                  {method.currencies && (
                    <div className="flex items-start gap-2.5 rounded-lg border border-line bg-surface-2 px-3 py-2.5">
                      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-faint" aria-hidden="true" />
                      <p className="text-xs leading-relaxed text-ink-soft">
                        Offered only on payments in {method.currencies.join(', ')}.
                      </p>
                    </div>
                  )}

                  {fields.length > 0 && (
                    <div className="grid gap-4 sm:grid-cols-2">
                      {fields.map((field) => (
                        <Field
                          key={field.key}
                          label={field.label}
                          required={field.required}
                          error={save.error?.details?.fields?.includes(field.label) ? 'Required' : undefined}
                        >
                          {(props) => (
                            <Input
                              {...props}
                              value={details[field.key] ?? ''}
                              placeholder={field.placeholder}
                              maxLength={field.maxLength}
                              autoComplete="off"
                              spellCheck={false}
                              onChange={(e) => {
                                setDetails((d) => ({ ...d, [field.key]: e.target.value }));
                                if (needs) setNeeds(null);
                              }}
                            />
                          )}
                        </Field>
                      ))}
                    </div>
                  )}

                  <Field label="Instructions for the customer" hint={method.instructionsHint}>
                    {(props) => (
                      <Textarea
                        {...props}
                        rows={3}
                        maxLength={1000}
                        value={instructions}
                        onChange={(e) => setInstructions(e.target.value)}
                      />
                    )}
                  </Field>

                  {/*
                    * Every value above is printed on the checkout page. Saying so
                    * here is the difference between a merchant writing "HDFC ····1234"
                    * and writing something they assumed only they would read.
                    */}
                  <p className="text-xs text-muted">
                    These details are shown to your customers at checkout, exactly as written.
                  </p>

                  <div className="flex items-center gap-2">
                    <Button loading={save.loading} disabled={!dirty} onClick={() => save.mutate({}).catch(() => {})}>
                      Save changes
                    </Button>
                    {dirty && <span className="text-xs text-muted">Unsaved changes</span>}
                  </div>
                </div>
              )}
            </CardBody>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

/**
 * Every way a customer can pay, and who fulfils each one.
 *
 * This screen used to list only the methods a merchant collects by hand, which
 * could not express the thing merchants actually ask for: the same method, UPI,
 * paid into their own account at no fee or through a gateway that confirms
 * itself. Method and route are now two separate choices on one card.
 */
export function PaymentMethodsPage() {
  const { data, error, loading, refetch, setData } = useResource('/dashboard/payment-methods');

  // Which cards are mid-edit. A bank account number typed and not saved is
  // exactly the thing somebody would be annoyed to lose to a stray reload.
  const [dirtyCards, setDirtyCards] = useState({});
  const onDirtyChange = useCallback((key, dirty) => {
    setDirtyCards((current) => (current[key] === dirty ? current : { ...current, [key]: dirty }));
  }, []);
  useUnsavedChanges(Object.values(dirtyCards).some(Boolean));

  return (
    <>
      <PageHero
        icon={Wallet}
        chip="Get paid"
        title="Payment methods"
        blurb="What your customers can pay with, and who takes it — a payment provider that confirms itself, or you, into your own account. Switch each one on for payment links, the till, or both."
      />

      {loading && <Loading label="Loading payment methods…" />}
      {error && <ErrorNotice error={error} title="Could not load payment methods" onRetry={refetch} />}

      {data && (
        <div className="space-y-4">
          {data.map((method) => (
            <MethodCard key={method.key} method={method} onSaved={setData} onDirtyChange={onDirtyChange} />
          ))}
        </div>
      )}
    </>
  );
}

export default PaymentMethodsPage;
